import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import {
  Job,
  JobUpsertParams,
  ListingType,
  RawJob,
  transformJobQuoteRawToJobQuote,
} from '../types/job';
import { BudgetRange } from '../types/quote';
import { JobStatus } from '../types/common';
import { useCreditStore } from './creditStore';

interface JobState {
  jobs: Job[];
  listingTypes: ListingType[];
  budgetRanges: BudgetRange[];
  loading: boolean;
  error: string | null;
  loadingListingTypes: boolean;
  loadingBudgetRanges: boolean;
  hasSettledListingTypesLoad: boolean;
  hasSettledBudgetRangesLoad: boolean;
  fetchJobs: () => Promise<void>;
  fetchListingTypes: (forced_refresh?: boolean) => Promise<void>;
  fetchBudgetRanges: (forced_refresh?: boolean) => Promise<void>;
  upsertJob: (
    jobData: JobUpsertParams,
    jobId?: string,
    options?: { refresh?: boolean }
  ) => Promise<{ jobId: string }>;
  publishJob: (
    jobId: string,
    listingTypeId: number,
    expiresAt?: string,
    selectionDate?: string,
    publicationRequestId?: string,
    options?: { refresh?: boolean }
  ) => Promise<'published' | 'already_processed'>;
  renewJob: (jobId: string) => Promise<void>;
  sendQuoteRequest: (
    freelancerId: string,
    jobParams: JobUpsertParams,
    requestMessage?: string
  ) => Promise<{ jobId: string; quoteRequestId: string }>;
  cancelJob: (jobId: string) => Promise<void>;
  pauseJob: (jobId: string) => Promise<void>;
  openJob: (jobId: string) => Promise<void>;
  startReviewingJob: (jobId: string) => Promise<void>;
  isJobOwner: (jobId: string) => Promise<boolean>;
}

export const useJobStore = create<JobState>((set, get) => ({
  jobs: [],
  listingTypes: [],
  budgetRanges: [],
  loading: false,
  error: null,
  filters: {},
  loadingListingTypes: false,
  loadingBudgetRanges: false,
  hasSettledListingTypesLoad: false,
  hasSettledBudgetRangesLoad: false,

  fetchListingTypes: async (forced_refresh = false) => {
    if ((!forced_refresh && get().listingTypes.length > 0) || get().loadingListingTypes) return;
    set({ loadingListingTypes: true });
    try {
      const { data, error } = await supabase
        .from('listing_types')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;
      set({ listingTypes: data });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loadingListingTypes: false, hasSettledListingTypesLoad: true });
    }
  },

  fetchBudgetRanges: async (forced_refresh = false) => {
    if ((!forced_refresh && get().budgetRanges.length > 0) || get().loadingBudgetRanges) return;
    set({ loadingBudgetRanges: true });
    try {
      const { data, error } = await supabase
        .from('budget_ranges')
        .select('*')
        .order('id', { ascending: true });

      if (error) throw error;
      set({ budgetRanges: data });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loadingBudgetRanges: false, hasSettledBudgetRangesLoad: true });
    }
  },

  fetchJobs: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const { data, error } = await supabase.rpc('fetch_user_jobs');

      if (error) throw error;

      // Transform the raw data into the Job interface structure
      const jobs: Job[] = (data as RawJob[]).map(jobData => ({
        id: jobData.id,
        title: jobData.title,
        description: jobData.description,
        on_site: jobData.on_site,
        start_date: jobData.start_date,
        end_date: jobData.end_date,
        published_at: jobData.published_at,
        expires_at: jobData.expires_at,
        selection_date: jobData.selection_date,
        completed_at: jobData.completed_at,
        status: jobData.status as JobStatus,
        languages: jobData.languages,
        visibility: jobData.visibility as 'public' | 'private',
        city: {
          id: jobData.city_id,
          name: jobData.city_name,
        },
        client: {
          id: jobData.client_id,
          first_name: jobData.client_first_name,
          last_name: jobData.client_last_name,
          company_name: jobData.client_company_name,
          avatar_url: jobData.client_avatar_url,
        },
        freelancer:
          jobData.quotes.length > 0
            ? {
                id: jobData.quotes[0].quote_owner_id,
                first_name: jobData.quotes[0].quote_owner_first_name,
                avatar_url: jobData.quotes[0].quote_owner_avatar_url,
              }
            : undefined,

        budget_range: {
          id: jobData.budget_range_id,
          code: jobData.budget_range_code,
          min_amount: jobData.budget_range_min_amount,
          max_amount: jobData.budget_range_max_amount,
        },
        required_services: jobData.required_services,
        listing_type: {
          id: jobData.listing_type_id,
          code: jobData.listing_type_code,
        },
        created_at: jobData.created_at,
        updated_at: jobData.created_at, // Using created_at as fallback since updated_at is not returned
        quotes: jobData.quotes.map(transformJobQuoteRawToJobQuote),
        quote_request_status: jobData.quote_request_status,
        shadow_recruiter_id: jobData.shadow_recruiter_id ?? null,
        shadow_recruiter_company_name: jobData.shadow_recruiter_company_name ?? null,
        is_shadow_recruiter: jobData.is_shadow_recruiter ?? false,
        recruiter_display_name: jobData.recruiter_display_name ?? null,
        recruiter_display_context: jobData.recruiter_display_context ?? null,
      }));

      set({ jobs, error: null });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  upsertJob: async (jobData, jobId, options = {}) => {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('upsert_job', {
      p_job_id: jobId || null,
      p_title: jobData.title,
      p_city_id: jobData.city.id,
      p_description: jobData.description,
      p_budget_range_id: jobData.budgetRange.id,
      p_service_category_ids: jobData.requiredServices.map(service => service.id),
      p_on_site: jobData.onSite,
      p_languages: jobData.languages,
      p_start_date: jobData.startDate,
      p_end_date: jobData.endDate,
      p_shadow_recruiter_id: jobData.shadowRecruiterId ?? null,
    });

    if (error) throw error;
    if (options.refresh !== false) await get().fetchJobs();
    return { jobId: data };
  },

  publishJob: async (
    jobId: string,
    listingTypeId: number,
    expiresAt?: string,
    selectionDate?: string,
    publicationRequestId = crypto.randomUUID(),
    options = {}
  ) => {
    const { data, error } = await supabase.rpc('publish_job', {
      p_job_id: jobId,
      p_listing_type_id: listingTypeId,
      p_expires_at: expiresAt ?? null,
      p_selection_date: selectionDate ?? null,
      p_publication_request_id: publicationRequestId,
    });
    if (error) {
      throw error;
    }
    if (options.refresh !== false) {
      await useCreditStore.getState().refreshBalance();
      await get().fetchJobs();
    }
    const result = Array.isArray(data) ? data[0] : data;
    return (result?.status === 'already_processed' ? 'already_processed' : 'published') as
      | 'published'
      | 'already_processed';
  },

  renewJob: async (jobId: string) => {
    const { error } = await supabase.rpc('renew_job', { p_job_id: jobId });
    if (error) {
      throw error;
    }
    await get().fetchJobs();
  },

  sendQuoteRequest: async (freelancerId: string, jobParams: JobUpsertParams) => {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('Not authenticated');

    const { data, error } = await supabase.rpc('create_private_job_with_quote_request', {
      p_title: jobParams.title,
      p_city_id: jobParams.city.id,
      p_description: jobParams.description,
      p_budget_range_id: jobParams.budgetRange.id,
      p_service_category_ids: jobParams.requiredServices.map(service => service.id),
      p_on_site: jobParams.onSite,
      p_languages: jobParams.languages,
      p_start_date: jobParams.startDate,
      p_end_date: jobParams.endDate,
      p_target_freelancer_id: freelancerId,
    });

    if (error) throw error;
    await get().fetchJobs();
    return { jobId: data[0].job_id, quoteRequestId: data[0].quote_request_id };
  },

  cancelJob: async (jobId: string) => {
    const { error } = await supabase.rpc('cancel_job', { p_job_id: jobId });

    if (error) {
      console.error('Erreur annulation job :', error);
      throw error;
    }
    await get().fetchJobs();
  },

  pauseJob: async (jobId: string) => {
    const { error } = await supabase.rpc('pause_job', { p_job_id: jobId });

    if (error) {
      console.error("Erreur de mise en pause de l'offre", error);
      throw error;
    }
    await get().fetchJobs();
  },

  openJob: async (jobId: string) => {
    const { error } = await supabase.rpc('open_job', { p_job_id: jobId });

    if (error) {
      console.error("Erreur d'activation de l'offre", error);
      throw error;
    }
    await get().fetchJobs();
  },

  startReviewingJob: async (jobId: string) => {
    const { error } = await supabase.rpc('start_reviewing_job', { p_job_id: jobId });

    if (error) {
      console.error('Erreur de passage en révision des soumissions', error);
      throw error;
    }
    await get().fetchJobs();
  },

  isJobOwner: async (jobId: string) => {
    // Deliberately not `fetch_user_jobs`/`search_jobs`: both are scoped for their own list
    // views (the former excludes in_progress/cancelled jobs, the latter is the
    // freelancer-facing search RPC restricted to open/public/published jobs) and would
    // report "not owner" for perfectly normal jobs of the requesting user. A direct read of
    // `jobs` is status-agnostic and relies on `jobs` RLS instead: the "Jobs are viewable by
    // their owners" policy (`client_id = auth.uid()`) makes a row come back for a recruiter
    // iff they own it. (Two other SELECT policies also apply to admins and to a freelancer
    // with a quote request on the job, so treat this as "can read this job under RLS" rather
    // than "owns it" outside the recruiter-only caller this was built for.)
    const { data, error } = await supabase.from('jobs').select('id').eq('id', jobId).maybeSingle();

    if (error) {
      console.error('Error checking job ownership:', error);
      return false;
    }

    return data !== null;
  },
}));
